import React from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import _ from 'lodash'

import { Form, Select, Button, Spin, Input, DatePicker } from 'antd'
import SelectInvoiceLineItemsContainer from '../../containers/Invoices/SelectInvoiceLineItemsContainer'
import SelectInvoiceShippingCostsContainer from '../../containers/Invoices/SelectInvoiceShippingCostsContainer'
import SelectInvoiceServiceLineItemsContainer from '../../containers/Invoices/SelectInvoiceServiceLineItemsContainer'
import SelectBankAccountContainer from '../../containers/SelectBankAccount'

// Mirrors InvoiceAdjustment#refund_channel in osh-online (credit notes only):
// cash -> bank required; credit_card -> wallet provider wins, else bank;
// wallet -> the fixed customer wallet, no extra input.
const REFUND_CHANNELS = [
    { value: 'none', labelKey: 'confirms:invoice.refundChannels.none' },
    { value: 'cash', labelKey: 'confirms:invoice.refundChannels.cash' },
    {
        value: 'credit_card',
        labelKey: 'confirms:invoice.refundChannels.creditCard'
    },
    { value: 'wallet', labelKey: 'confirms:invoice.refundChannels.wallet' }
]

const REFUND_WALLET_PROVIDERS = [
    { value: 'xendit', labelKey: 'confirms:invoice.refundWalletProviders.xendit' },
    {
        value: 'xendit_gbprime',
        labelKey: 'confirms:invoice.refundWalletProviders.xenditGbprime'
    },
    {
        value: 'true_money',
        labelKey: 'confirms:invoice.refundWalletProviders.trueMoney'
    }
]

const OrderInvoiceAdjustmentForm = ({
    errors = {},
    i18n,
    invoiceAdjustment = {},
    items = [],
    loading = false,
    onSubmit = () => {},
    orderId,
    saving = false,
    onInvoiceAdjustmentChange,
    adjustmentTypeSelected,
    debitNotePurposes,
    creditNotePurposes
}) => {
    const [form] = Form.useForm()

    const handleSubmit = (values) => {
        onSubmit(values)
    }

    const fieldError = (name) => {
        const messages = errors[name]

        return messages ? _.castArray(messages).join(' ') : undefined
    }

    const renderPurposesSelect = (items) => {
        return items.map((item) => {
            return (
                <Select.Option value={item.get('name')}>
                    {item.get('label')}
                </Select.Option>
            )
        })
    }

    const renderPurposes = (selected) => {
        if (selected === 'debit_note') {
            return (
                <Form.Item
                    name='debit_note_purpose_code'
                    initialValue={_.get(
                        invoiceAdjustment,
                        'debit_note_purpose_code'
                    )}
                    label={i18n.t('confirms:invoice.purpose')}>
                    <Select
                        placeholder={i18n.t('confirms:invoice.adjustmentType')}>
                        {renderPurposesSelect(debitNotePurposes)}
                    </Select>
                </Form.Item>
            )
        } else if (selected === 'credit_note') {
            return (
                <Form.Item
                    name='credit_note_purpose_code'
                    initialValue={_.get(
                        invoiceAdjustment,
                        'credit_note_purpose_code'
                    )}
                    label={i18n.t('confirms:invoice.purpose')}>
                    <Select
                        placeholder={i18n.t('confirms:invoice.adjustmentType')}>
                        {renderPurposesSelect(creditNotePurposes)}
                    </Select>
                </Form.Item>
            )
        } else {
            return null
        }
    }

    const renderRefundBankAccount = (channel) => {
        const bankAccountId = _.get(invoiceAdjustment, 'refund_bank_account_id')
        const rules =
            channel === 'cash'
                ? [
                      {
                          required: true,
                          message: i18n.t(
                              'confirms:invoice.pleaseSelectRefundBank'
                          )
                      }
                  ]
                : [
                      ({ getFieldValue }) => ({
                          validator: (rule, value) =>
                              value || getFieldValue('refund_wallet_provider')
                                  ? Promise.resolve()
                                  : Promise.reject(
                                        new Error(
                                            i18n.t(
                                                'confirms:invoice.pleaseSelectRefundBankOrWallet'
                                            )
                                        )
                                    )
                      })
                  ]

        return (
            <Form.Item
                name='refund_bank_account_id'
                initialValue={
                    bankAccountId ? _.toString(bankAccountId) : undefined
                }
                label={i18n.t('confirms:invoice.refundBankAccount')}
                dependencies={['refund_wallet_provider']}
                validateStatus={fieldError('refund_bank_account_id') && 'error'}
                help={fieldError('refund_bank_account_id')}
                rules={rules}>
                <SelectBankAccountContainer />
            </Form.Item>
        )
    }

    const renderRefundWalletProvider = () => {
        return (
            <Form.Item
                name='refund_wallet_provider'
                initialValue={_.get(
                    invoiceAdjustment,
                    'refund_wallet_provider'
                )}
                label={i18n.t('confirms:invoice.refundWalletProvider')}
                validateStatus={fieldError('refund_wallet_provider') && 'error'}
                help={fieldError('refund_wallet_provider')}>
                <Select
                    allowClear
                    placeholder={i18n.t(
                        'confirms:invoice.refundWalletProvider'
                    )}>
                    {REFUND_WALLET_PROVIDERS.map((provider) => (
                        <Select.Option
                            key={provider.value}
                            value={provider.value}>
                            {i18n.t(provider.labelKey)}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        )
    }

    const renderRefundChannel = (selected) => {
        if (selected !== 'credit_note') {
            return null
        }

        return (
            <>
                <Form.Item
                    name='refund_channel'
                    initialValue={
                        _.get(invoiceAdjustment, 'refund_channel') || 'none'
                    }
                    label={i18n.t('confirms:invoice.refundChannel')}
                    validateStatus={fieldError('refund_channel') && 'error'}
                    help={fieldError('refund_channel')}>
                    <Select
                        placeholder={i18n.t(
                            'confirms:invoice.refundChannel'
                        )}>
                        {REFUND_CHANNELS.map((channel) => (
                            <Select.Option
                                key={channel.value}
                                value={channel.value}>
                                {i18n.t(channel.labelKey)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    noStyle
                    shouldUpdate={(prev, current) =>
                        prev.refund_channel !== current.refund_channel
                    }>
                    {({ getFieldValue }) => {
                        const channel = getFieldValue('refund_channel')

                        return (
                            <>
                                {channel === 'credit_card' &&
                                    renderRefundWalletProvider()}
                                {(channel === 'cash' ||
                                    channel === 'credit_card') &&
                                    renderRefundBankAccount(channel)}
                                {channel === 'wallet' && (
                                    <p style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                                        {i18n.t(
                                            'confirms:invoice.refundWalletHint'
                                        )}
                                    </p>
                                )}
                            </>
                        )
                    }}
                </Form.Item>
            </>
        )
    }

    if (loading) {
        return (
            <div style={{ width: '100%', textAlign: 'center' }}>
                <Spin />
            </div>
        )
    }

    dayjs.locale(i18n.language)
    const date = _.get(invoiceAdjustment, 'adjustment_date')

    return (
        <>
            <h2>{i18n.t('confirms:invoice.invoiceAdjustment')}</h2>
            <Form onFinish={handleSubmit} layout='vertical' form={form}>
                <Form.Item
                    name='adjustment_type'
                    initialValue={_.get(invoiceAdjustment, 'adjustment_type')}
                    label={i18n.t('confirms:invoice.adjustmentType')}
                    validateStatus={fieldError('adjustment_type') && 'error'}
                    help={fieldError('adjustment_type')}
                    rules={[
                        {
                            required: true,
                            message: i18n.t(
                                'confirms:invoice.pleaseSelectAdjustmentType'
                            )
                        }
                    ]}>
                    <Select
                        placeholder={i18n.t('confirms:invoice.adjustmentType')}
                        onChange={onInvoiceAdjustmentChange}>
                        <Select.Option value='debit_note'>
                            {i18n.t(
                                'confirms:invoice.adjustmentTypes.debitNote'
                            )}
                        </Select.Option>
                        <Select.Option value='credit_note'>
                            {i18n.t(
                                'confirms:invoice.adjustmentTypes.creditNote'
                            )}
                        </Select.Option>
                    </Select>
                </Form.Item>
                {renderPurposes(
                    adjustmentTypeSelected ||
                        _.get(invoiceAdjustment, 'adjustment_type')
                )}
                {renderRefundChannel(
                    adjustmentTypeSelected ||
                        _.get(invoiceAdjustment, 'adjustment_type')
                )}
                <Form.Item
                    label={i18n.t('confirms:invoice.adjustmentDate')}
                    name='adjustment_date'
                    initialValue={date && dayjs(date)}
                    validateStatus={fieldError('adjustment_date') && 'error'}
                    help={fieldError('adjustment_date')}
                    rules={[
                        {
                            required: true,
                            message: i18n.t(
                                'confirms:invoice.pleaseSelectAdjustmentDate'
                            )
                        }
                    ]}>
                    <DatePicker
                        className='w-100'
                        placeholder={i18n.t('confirms:invoice.selectDate')}
                    />
                </Form.Item>
                <Form.Item
                    label={i18n.t('confirms:invoice.refNo')}
                    name='ref_no'
                    initialValue={_.get(invoiceAdjustment, 'ref_no')}
                    validateStatus={fieldError('ref_no') && 'error'}
                    help={fieldError('ref_no')}>
                    <Input />
                </Form.Item>
                <Form.Item
                    label={i18n.t('confirms:invoice.discount')}
                    initialValue={_.get(
                        invoiceAdjustment,
                        'money_display.discount'
                    )}
                    name='discount'
                    validateStatus={fieldError('discount') && 'error'}
                    help={fieldError('discount')}>
                    <Input />
                </Form.Item>
                <Form.Item
                    label={i18n.t('confirms:invoice.note')}
                    name='note'
                    initialValue={_.get(invoiceAdjustment, 'note')}
                    validateStatus={fieldError('note') && 'error'}
                    help={fieldError('note')}>
                    <Input.TextArea />
                </Form.Item>
                <Form.Item
                    name='items'
                    initialValue={items.filter(
                        (item) => item.item_type === 'LineItem' || item.item_type === 'ServiceLineItem'
                    )}>
                    <SelectInvoiceLineItemsContainer orderId={orderId} />
                </Form.Item>
                <Form.Item
                    name='shipping_costs'
                    initialValue={items.filter(
                        (item) => item.item_type === 'ShippingCost'
                    )}>
                    <SelectInvoiceShippingCostsContainer orderId={orderId} />
                </Form.Item>
                <Form.Item
                    name='service_items'
                    label={i18n.t('inquiries:serviceLineItems')}
                    initialValue={items.filter(
                        (item) => item.item_type === 'ServiceLineItem'
                    )}>
                    <SelectInvoiceServiceLineItemsContainer orderId={orderId} />
                </Form.Item>

                <Button htmlType='submit' loading={saving} type='primary'>
                    {i18n.t('shared:save')}
                </Button>
            </Form>
        </>
    )
}

OrderInvoiceAdjustmentForm.propTypes = {
    errors: PropTypes.object,
    form: PropTypes.object,
    invoiceAdjustment: PropTypes.object,
    items: PropTypes.array,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    orderId: PropTypes.string,
    saving: PropTypes.bool
}

export default withTranslation(['confirms', 'inquiries', 'shared'])(
    OrderInvoiceAdjustmentForm
)
